import $ from "jquery";
import {AddressConfirmationModal} from "./address-confirmation-modal";
import {ErrorModal} from "./error-modal";
import {Cart} from "./cart";

/** @abstract */
export class BaseCheckout {
    static get VALIDATION_ERROR_SHIPPING_COUNTRY_CHANGED() { return 1; }
    static get VALIDATION_ERROR_FULL_NAME_REQUIRED() { return 2; }
    static get VALIDATION_ERROR_EMAIL_REQUIRED() { return 3; }
    static get VALIDATION_ERROR_EMAIL_INVALID() { return 4; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_STREET_REQUIRED() { return 5; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_CITY_REQUIRED() { return 6; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_BAD_COUNTRY() { return 7; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_UNSUPPORTED_COUNTRY() { return 8; }
    static get VALIDATION_ERROR_LEGALS_NOT_ACCEPTED() { return 9; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_BAD_POSTAL_CODE() { return 10; }
    static get VALIDATION_ERROR_SHIPPING_PHONE_REQUIRED() { return 11; }
    static get VALIDATION_ERROR_CART_EMPTY() { return 12; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_STATE_MISMATCH() { return 13; }

    static get VALIDATION_SHIPPING_ADDRESS_ERROR () { return 20; }
    static get VALIDATION_ERROR_SHIPPING_ADDRESS_NOT_SERVED() { return 22; }

    static get VALIDATION_ERROR_EXCLUSIVE_DISTRIBUTOR() { return 30; }


    constructor(element, cart, ajaxURL) {
        this.element = $(element);
        /** @type Cart */
        this.cart = cart;
        this.ajaxURL = ajaxURL;
        this.hasPendingAjaxRequests = false;
        /** @type AddressConfirmationModal|null */
        this.addressConfirmationModal = null;
        /** @type HTMLInputElement|HTMLSelectElement|null the field showFieldError() marked invalid */
        this.fieldErrorInput = null;
        /** @type ErrorModal|null */
        this.errorModal = $('#error-modal').length ? new ErrorModal($('#error-modal')) : null;

        this.element.find('input')
            .on('focus', function(event) {
                $(event.target).removeClass('full-placeholder');
            } )
            .on('blur', function(event) {
                let element = $(event.target);
                element.toggleClass('full-placeholder', element.val() === '');
            } )
            .each(function(idx, element) {
                element = $(element);
                if (!element.val()) element.addClass('full-placeholder');
            });

        this.element.on('submit', this.onFormSubmit.bind(this));
        this.submitButton = this.element.find('button.submit');
    }

    showLoading () {
        this.element.addClass('loading');
    }

    hideLoading () {
        this.element.removeClass('loading');
    }

    /**
     *
     * @param postData{{action: string, data?: string}}
     *
     * @returns {Promise<ServerReturnData>}
     */
    async postToServer(postData) {
        if (this.fetchAbortController) {
            this.fetchAbortController.abort();
        }
        const thisRequestAbortController = new AbortController();
        this.fetchAbortController = thisRequestAbortController;

        this.lastRequestIndex = this.lastRequestIndex + 1;
        this.submitButton.prop('disabled', true);
        this.submitButton.addClass('loading');

        return fetch (this.ajaxURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(postData),
            signal: thisRequestAbortController.signal
        }).then(response => {
            if (!response.ok) {
                console.error('Failed to sync cart with server:', response.status, response.statusText);
                return;
            }
            return response.json();
        }).then(res => {
            console.log('Synced with server');
            this.submitButton.prop('disabled', false);
            this.submitButton.removeClass('loading');
            return res;
        }).catch(error => {
            if (thisRequestAbortController.signal.aborted) {
                console.log('Request aborted');
                return;
            }
            console.error('Failed to sync cart with server:', error);
            this.submitButton.prop('disabled', false);
            this.submitButton.removeClass('loading');
        });
    }

    async onFormSubmit(event) {
        event.preventDefault();

        if (this.hasPendingAjaxRequests) {
            return;
        }

        const submitErrors = await this.getSubmitValidationErrors();
        if (submitErrors.length) {
            submitErrors[0].focus();
            return;
        }

        this.showLoading();

        const postData = {action: 'submit', data: this.element.serialize()};
        // a confirmation applies to the address it was given for only
        this.element.find('input.js-force-address').remove();
        this.postToServer(postData).then(res => {
            // postToServer() resolves without data when the request failed
            if (!res) {
                this.hideLoading();
                this.showRequestFailedMessage();
                return;
            }
            if (res.redirectTo) {
                this.trackSuccessfulCheckout(res);

                setTimeout(function () {
                    window.location = res.redirectTo;
                }, 500);
            } else {
                this.hideLoading();

                if (res.errors) {
                    try {
                        gtag('send', 'checkout_address_error', {event_category: 'ecommerce'});
                    } catch (e) {
                    }

                    this.handlePostErrorFromServer(res);
                } else {
                    try {
                        gtag('send', 'checkout_internal_error', {event_category: 'ecommerce'});
                    } catch (e) {
                    }
                    this.showRequestFailedMessage();
                }
            }
        }).catch(error => {
            console.error('Failed to submit checkout form:', error);
            this.hideLoading();
            this.showRequestFailedMessage();
        });
    }

    /**
     * @param errorText string
     * @param title string|null replaces the dialog's default title ("Please check your order details")
     */
    showErrorMessage(errorText, title = null) {
        if (this.errorModal) {
            this.errorModal.open(errorText, title);
        } else {
            alert(title ? title + '. ' + errorText : errorText);
        }
    }

    showRequestFailedMessage() {
        this.showErrorMessage('Please try again later or contact support.', 'Something went wrong');
    }

    /**
     * Shows the message as the browser's validation bubble on the field and keeps the field invalid
     * until the customer changes something in the form.
     * @param field jQuery
     * @param message string
     * @return boolean false when the field is not on the page
     */
    showFieldError(field, message) {
        const input = field.get(0);
        if (!input || !input.setCustomValidity) {
            return false;
        }
        // one field error at a time: a new one replaces the previous error and its handler
        if (this.fieldErrorInput) {
            this.fieldErrorInput.setCustomValidity('');
        }
        this.fieldErrorInput = input;
        input.setCustomValidity(message);
        // any edit clears it, not only one to this field: the fix may be in another field (the ZIP for a
        // state error), and the server checks again on the next submit
        this.element.off('.fieldError').one('input.fieldError change.fieldError', () => {
            input.setCustomValidity('');
            this.fieldErrorInput = null;
            this.element.off('.fieldError');
        });
        // scrolled instantly: reportValidity() scrolls too and would cut a smooth scroll short,
        // leaving the field at the screen edge with its bubble off-screen
        const top = input.getBoundingClientRect().top + window.scrollY - window.innerHeight / 3;
        window.scrollTo({top: Math.max(0, top), behavior: 'instant'});
        input.focus({preventScroll: true});
        input.reportValidity();
        return true;
    }

    /**
     * @abstract
     * @param res ServerReturnData
     */
    handlePostErrorFromServer(res) {
        if (res.errors && res.errors.length > 0) {
            const validationError = res.errors[0];
            switch (validationError.code) {
                case BaseCheckout.VALIDATION_ERROR_SHIPPING_ADDRESS_BAD_POSTAL_CODE:
                    if (!this.showFieldError(this.element.find('[name=shippingZip]'), validationError.message)) {
                        this.showErrorMessage(validationError.message);
                    }
                    break;
                case BaseCheckout.VALIDATION_ERROR_SHIPPING_ADDRESS_STATE_MISMATCH:
                    if (!this.showFieldError(this.element.find('[name=shippingState]'), validationError.message)) {
                        this.showErrorMessage(validationError.message);
                    }
                    break;
                case BaseCheckout.VALIDATION_SHIPPING_ADDRESS_ERROR: // Address validation error
                    this.addressConfirmationModal.open(validationError)
                        .then((confirmed) => {
                            if (confirmed) {
                                console.log('Force address');
                                // user has confirmed to proceed
                                this.element.append($('<input type="hidden" name="forceAddress" value="1" class="js-force-address" />'));
                                this.element.trigger('submit');
                            } else {
                                console.log('Double-check address');
                            }
                        });
                    break;
                default:
                    this.showErrorMessage(res.errors[0].message);
            }
        } else {
            this.showRequestFailedMessage();
        }
/*
        if (res.errors.validation && res.errors.validation.length > 0) {
            this.showErrorMessage(res.errors.validation[0].message);
        } else if (res.errors.address === 2) {
            this.showErrorMessage('Please fill in all shipping address fields');
        } else if ((res.errors.address === 1 || res.errors.address === 3) && this.addressConfirmationModal != null) {
            // present the modal for confirming the address
            this.addressConfirmationModal.open(res.errors)
                .then((confirmed) => {
                    if (confirmed) {
                        console.log('Force address');
                        // user has confirmed to proceed
                        this.element.append($('<input type="hidden" name="forceAddress" value="1" class="js-force-address" />'));
                        this.element.trigger('submit');
                    } else {
                        console.log('Double-check address');
                    }
                });
        } else {
            this.showErrorMessage('Something went wrong. Please try again later or contact support');
        }*/
    }

    // this is marked async so it can be overridden by subclasses
    async getSubmitValidationErrors() {
        let submitErrors = [];

        this.element.find('input:required').each(function() {
            const element = $(this);
            let val = element.val();
            if (val.length === 0) {
                submitErrors.push(element);
            } else if (element.attr('type') === 'email') {
                if (! /\S+@\S+\.\S+/.test(val)) {
                    submitErrors.push(element);
                }
            }
        });

        return submitErrors;
    }

    trackSuccessfulCheckout(res) {
        if (!res.items) {
            return;
        }
        let i;
        try {
            const gtagcoupon = (res.hasDiscount ? res.items[0].discountCode.code : '');
            const gtagitems = [];
            for (i = 0; i < res.items.length; i++) {
                let item = res.items[i];
                gtagitems.push({
                    id: item.sku,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price.value
                });
            }
            const gtagdata = {
                items: gtagitems,
                currency: res.items[0].price.currency,
                coupon: gtagcoupon
            };
            gtag('event', 'checkout_progress', gtagdata);
            console.log(gtagdata);
        } catch (error) {
            console.error(error);
        }
    }
}

/**
 * @typedef {Object} ServerReturnData
 * @property {string} total
 * @property {number} totalFl
 * @property {string} shipping
 * @property {number} shippingFl
 * @property {string|null} importDutyAndTaxes
 * @property {number|null} importDutyAndTaxesFl
 * @property {boolean} freeShipping
 * @property {boolean} hasDiscount
 * @property {boolean} discountApplies
 * @property {CourierData[]} couriersData
 * @property {string} shippingTermsText
 * @property {DestinationData} destination
 *
 * @typedef {Object} CourierData
 * @property {string} name
 * @property {string} code
 * @property {boolean} selected
 *
 * @typedef {Object} DestinationData
 * @property {boolean} postalCodeRequired
 * @property {boolean} supported
 * @property {boolean} couriersVaryPerPostCodePrefix
 * @property {string} courier
 * @property {{name: string, website: string} | null} exclusiveDistributor
 *
 * @property {boolean} discountCodeAccepted Only if action is apply-discount-code
 * @property {string} redirectTo Only if action is submit
 * @property {ErrorsObject} errors
 *
 * @typedef {Object} ErrorsObject
 * @property {string[]} validation
 * @property {number} address
 * @property {boolean} distributor
 */